import {
  AUTH_ACCESS_TOKEN_STORAGE_KEY,
  AUTH_ENTERPRISE_SSO_SESSION_STORAGE_KEY,
  AUTH_OTP_VERIFIED_LEGACY_STORAGE_KEY,
  AUTH_OTP_VERIFIED_STORAGE_KEY,
  AUTH_PENDING_BACKUP_CODES_STORAGE_KEY,
  AUTH_PENDING_BACKUP_CODES_TTL_MS,
  AUTH_PENDING_SIGN_IN_IDENTIFIER_STORAGE_KEY,
  AUTH_PENDING_SIGN_IN_IDENTIFIER_TTL_MS,
  AUTH_REFRESH_TOKEN_STORAGE_KEY,
  AUTH_SIGN_IN_REMEMBER_STORAGE_KEY,
  AUTH_STORAGE_PERSISTENCE_LOCAL,
  AUTH_STORAGE_PERSISTENCE_SESSION,
  type AuthTokenPersistence,
} from 'common/constants/auth/storage';

const AUTH_SESSION_SYNC_CHANNEL_NAME = 'scalar-auth-session-sync';

// JWT tokens use localStorage when "keep signed in" is enabled (shared across tabs).
// Unchecked sign-in keeps tokens in sessionStorage (tab-local). Cross-tab sync is only
// used to fan out logout events so sibling tabs do not keep stale authenticated UI.
type AuthSessionSyncMessage =
  | { type: 'logout' };

let authSessionSyncChannel: BroadcastChannel | null | undefined;

// Read both names during the migration so existing persisted sessions keep their 2FA state
// until every active bundle writes and reads only the canonical key.
const AUTH_OTP_VERIFIED_STORAGE_KEYS = [
  AUTH_OTP_VERIFIED_STORAGE_KEY,
  AUTH_OTP_VERIFIED_LEGACY_STORAGE_KEY,
] as const;

interface PendingSignInIdentifierState {
  expiresAt: number;
  identifier: string;
}

interface PendingBackupCodesState {
  backupCodes: string[];
  expiresAt: number;
}

const AUTH_TOKEN_STORAGE_KEYS = [
  AUTH_ACCESS_TOKEN_STORAGE_KEY,
  AUTH_REFRESH_TOKEN_STORAGE_KEY,
] as const;

const getStorageByPersistence = (persistence: AuthTokenPersistence): Storage =>
  persistence === AUTH_STORAGE_PERSISTENCE_SESSION ? sessionStorage : localStorage;

const migrateRememberSignInPreferenceFromSession = () => {
  const sessionValue = sessionStorage.getItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY);

  if (sessionValue !== null && localStorage.getItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY) === null) {
    localStorage.setItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY, sessionValue);
  }

  sessionStorage.removeItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY);
};

export const getRememberSignInPreference = () => {
  migrateRememberSignInPreferenceFromSession();

  const value = localStorage.getItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY);

  if (value === null) {
    return true;
  }

  return value === '1';
};

const getStoredToken = (key: string) => {
  migrateRememberSignInPreferenceFromSession();

  if (getRememberSignInPreference()) {
    const sessionValue = sessionStorage.getItem(key);
    const localValue = localStorage.getItem(key);

    if (sessionValue && !localValue) {
      localStorage.setItem(key, sessionValue);
      sessionStorage.removeItem(key);
      return sessionValue;
    }

    return localValue ?? sessionValue;
  }

  return sessionStorage.getItem(key);
};

const clearStoredToken = (key: string) => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

const hasTrueStoredFlag = (storage: Storage, keys: readonly string[]) =>
  keys.some(key => storage.getItem(key) === 'true');

const setStoredFlag = ({
  keys,
  storage,
  value,
}: {
  keys: readonly string[];
  storage: Storage;
  value: boolean;
}) => {
  keys.forEach(key => {
    if (!value) {
      storage.removeItem(key);
      return;
    }

    // Keep the legacy auth flag in sync during rollout so existing tabs and cached bundles do not disagree on 2FA state.
    storage.setItem(key, 'true');
  });
};

// Refresh flows must keep writing tokens back to the same storage scope used by the active session.
const getActiveTokenPersistence = (): AuthTokenPersistence => {
  if (sessionStorage.getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY) || sessionStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)) {
    return AUTH_STORAGE_PERSISTENCE_SESSION;
  }

  return AUTH_STORAGE_PERSISTENCE_LOCAL;
};

const getAuthSessionSyncChannel = () => {
  if (authSessionSyncChannel !== undefined) {
    return authSessionSyncChannel;
  }

  authSessionSyncChannel = typeof BroadcastChannel === 'undefined'
    ? null
    : new BroadcastChannel(AUTH_SESSION_SYNC_CHANNEL_NAME);

  return authSessionSyncChannel;
};

export const setEnterpriseSsoSignInSession = () => {
  sessionStorage.setItem(AUTH_ENTERPRISE_SSO_SESSION_STORAGE_KEY, '1');
};

export const clearEnterpriseSsoSignInSession = () => {
  sessionStorage.removeItem(AUTH_ENTERPRISE_SSO_SESSION_STORAGE_KEY);
};

export const isEnterpriseSsoSignInSession = () =>
  sessionStorage.getItem(AUTH_ENTERPRISE_SSO_SESSION_STORAGE_KEY) === '1';

export const isOtpVerified = () => hasTrueStoredFlag(localStorage, AUTH_OTP_VERIFIED_STORAGE_KEYS);

export const setOtpVerified = () => {
  setStoredFlag({
    keys: AUTH_OTP_VERIFIED_STORAGE_KEYS,
    storage: localStorage,
    value: true,
  });
};

export const clearOtpVerified = () => {
  setStoredFlag({
    keys: AUTH_OTP_VERIFIED_STORAGE_KEYS,
    storage: localStorage,
    value: false,
  });
};

export const getStoredAccessToken = () => getStoredToken(AUTH_ACCESS_TOKEN_STORAGE_KEY);

export const setStoredAccessToken = (
  accessToken: string,
  persistence: AuthTokenPersistence = getActiveTokenPersistence()
) => {
  clearStoredToken(AUTH_ACCESS_TOKEN_STORAGE_KEY);
  getStorageByPersistence(persistence).setItem(AUTH_ACCESS_TOKEN_STORAGE_KEY, accessToken);
};

export const clearStoredAccessToken = () => {
  clearStoredToken(AUTH_ACCESS_TOKEN_STORAGE_KEY);
};

export const getStoredRefreshToken = () => getStoredToken(AUTH_REFRESH_TOKEN_STORAGE_KEY);

export const setStoredRefreshToken = (
  refreshToken: string,
  persistence: AuthTokenPersistence = getActiveTokenPersistence()
) => {
  clearStoredToken(AUTH_REFRESH_TOKEN_STORAGE_KEY);
  getStorageByPersistence(persistence).setItem(AUTH_REFRESH_TOKEN_STORAGE_KEY, refreshToken);
};

export const clearStoredRefreshToken = () => {
  clearStoredToken(AUTH_REFRESH_TOKEN_STORAGE_KEY);
};

export const setStoredAuthTokens = ({
  accessToken,
  refreshToken,
  persistence = AUTH_STORAGE_PERSISTENCE_LOCAL,
}: {
  accessToken: string;
  refreshToken: string;
  persistence?: AuthTokenPersistence;
}) => {
  setStoredAccessToken(accessToken, persistence);
  setStoredRefreshToken(refreshToken, persistence);
};

export const clearStoredAuthTokens = () => {
  clearStoredAccessToken();
  clearStoredRefreshToken();
};

export const getSignInTokenPersistence = (
  rememberSignIn = getRememberSignInPreference()
): AuthTokenPersistence =>
  rememberSignIn ? AUTH_STORAGE_PERSISTENCE_LOCAL : AUTH_STORAGE_PERSISTENCE_SESSION;

/** Align stored JWT scope with the remember-sign-in preference (runs on app init and checkbox change). */
export const syncAuthTokensWithRememberPreference = (
  rememberSignIn = getRememberSignInPreference()
) => {
  AUTH_TOKEN_STORAGE_KEYS.forEach(key => {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (rememberSignIn) {
      if (sessionValue && !localValue) {
        localStorage.setItem(key, sessionValue);
      }

      sessionStorage.removeItem(key);
      return;
    }

    if (localValue) {
      if (!sessionValue) {
        sessionStorage.setItem(key, localValue);
      }

      localStorage.removeItem(key);
    }
  });
};

export const setRememberSignInPreference = (value: boolean) => {
  migrateRememberSignInPreferenceFromSession();
  localStorage.setItem(AUTH_SIGN_IN_REMEMBER_STORAGE_KEY, value ? '1' : '0');
  syncAuthTokensWithRememberPreference(value);
};

export const clearPendingSignInIdentifier = () => {
  sessionStorage.removeItem(AUTH_PENDING_SIGN_IN_IDENTIFIER_STORAGE_KEY);
};

export const setPendingSignInIdentifier = (
  identifier: string,
  ttlMs = AUTH_PENDING_SIGN_IN_IDENTIFIER_TTL_MS
) => {
  const normalizedIdentifier = identifier.trim();

  if (!normalizedIdentifier) {
    clearPendingSignInIdentifier();
    return;
  }

  sessionStorage.setItem(
    AUTH_PENDING_SIGN_IN_IDENTIFIER_STORAGE_KEY,
    JSON.stringify({
      expiresAt: Date.now() + ttlMs,
      identifier: normalizedIdentifier,
    } satisfies PendingSignInIdentifierState)
  );
};

export const getPendingSignInIdentifier = () => {
  const rawValue = sessionStorage.getItem(AUTH_PENDING_SIGN_IN_IDENTIFIER_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<PendingSignInIdentifierState>;

    if (typeof parsedValue.identifier !== 'string' || typeof parsedValue.expiresAt !== 'number') {
      clearPendingSignInIdentifier();
      return null;
    }

    if (parsedValue.expiresAt <= Date.now()) {
      clearPendingSignInIdentifier();
      return null;
    }

    return parsedValue.identifier;
  } catch {
    clearPendingSignInIdentifier();
    return null;
  }
};

export const clearPendingBackupCodes = () => {
  sessionStorage.removeItem(AUTH_PENDING_BACKUP_CODES_STORAGE_KEY);
};

export const setPendingBackupCodes = (
  backupCodes: string[],
  ttlMs = AUTH_PENDING_BACKUP_CODES_TTL_MS
) => {
  const normalizedBackupCodes = backupCodes.map(code => code.trim()).filter(Boolean);

  if (normalizedBackupCodes.length === 0) {
    clearPendingBackupCodes();
    return;
  }

  sessionStorage.setItem(
    AUTH_PENDING_BACKUP_CODES_STORAGE_KEY,
    JSON.stringify({
      backupCodes: normalizedBackupCodes,
      expiresAt: Date.now() + ttlMs,
    } satisfies PendingBackupCodesState)
  );
};

export const getPendingBackupCodes = () => {
  const rawValue = sessionStorage.getItem(AUTH_PENDING_BACKUP_CODES_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<PendingBackupCodesState>;

    if (
      !Array.isArray(parsedValue.backupCodes)
      || parsedValue.backupCodes.some(code => typeof code !== 'string' || code.trim().length === 0)
      || typeof parsedValue.expiresAt !== 'number'
    ) {
      clearPendingBackupCodes();
      return null;
    }

    if (parsedValue.expiresAt <= Date.now()) {
      clearPendingBackupCodes();
      return null;
    }

    return parsedValue.backupCodes;
  } catch {
    clearPendingBackupCodes();
    return null;
  }
};

export const hasStoredAccessToken = () => Boolean(getStoredAccessToken());

export const subscribeToAuthSessionSync = ({
  onRemoteLogout,
}: {
  onRemoteLogout?: () => void;
} = {}) => {
  const channel = getAuthSessionSyncChannel();
  if (!channel) {
    return () => undefined;
  }

  const handleMessage = (event: MessageEvent<AuthSessionSyncMessage>) => {
    const message = event.data;

    if (message.type === 'logout') {
      clearStoredAuthTokens();
      clearOtpVerified();
      clearEnterpriseSsoSignInSession();
      onRemoteLogout?.();
    }
  };

  channel.addEventListener('message', handleMessage);

  return () => {
    channel.removeEventListener('message', handleMessage);
  };
};

export const broadcastAuthLogout = () => {
  getAuthSessionSyncChannel()?.postMessage({ type: 'logout' } satisfies AuthSessionSyncMessage);
};
